import Div from '@ui/layout/Div'
import { func, object } from 'prop-types'
import Modal from '@ui/component/Modal'
import Buttons from '@ui/elements/Buttons'
import Icon from '@ui/elements/Icon'
import TextType from '@ui/inputs/TextType'
import { useDispatch, useSelector } from 'react-redux'
import {
  updateSterilizationCompanyCallBuildings,
  updateSterilizationPayment,
  updateSterilizationCompanyCallFormObject
} from '@store/slice/sterilizationSlice'
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useTranslation } from 'react-i18next'
import { post, get } from '@util/xhrAPI'
import useToggle from '@hook/useToggle'
import useFormData from "@hook/useFormData"
import useCostCalculator from "@hook/useCostCalculator"
import { ROLE_PUBLIC } from "@router/Routes"
import { useNavigate } from 'react-router'
import videoBuildingsMap from '@assets/media/buildingsMap.mp4'
import { useEffect, useState } from 'react'

const CompanySterilizationCallBuildingsMapManager = ({ activeBuilding, setActiveBuilding }) => {
  const { sterilizationCompanyCallBuildings, sterilizationCompanyCallFormObject, selectedSeason } = useSelector(state => state.sterilization)
  const { missionBillings, mission } = useSelector(state => state.main)
  const { callType } = sterilizationCompanyCallFormObject
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [t] = useTranslation('sterilization')
  const { path, area, name } = activeBuilding
  const [isListing, toggleListing] = useToggle(false)
  const [isLoading, toggleLoading] = useToggle(false)
  const [isModalActive, toggleModal] = useToggle(false)
  const renderFormData = useFormData()
  const [freeCode, setFreeCode] = useState(null)
  const [cost, setCost] = useState(null)
  const [totalArea, setTotalArea] = useState(0)
  const calculateCost = useCostCalculator()
  const sterilizationCallId = window.localStorage.getItem('lastCompanyCall')


  const addToBuildingsList = async () => {
    const buildingsList = sterilizationCompanyCallBuildings.filter(building => building.key !== activeBuilding.key)
    await dispatch(updateSterilizationCompanyCallBuildings([
      ...buildingsList,
      { ...activeBuilding, key: Math.random().toString(36).substring(2, 7) }
    ]))
    if (!isListing) {
      toggleListing()
    }
    setTotalArea(totalArea + parseFloat(activeBuilding.area))
    resetPath()
  }

  const toggleBuilding = building => setActiveBuilding({ ...building })
  const removeBuilding = buildingKey => {
    const buildingsList = sterilizationCompanyCallBuildings.filter(building => {
      if (building.key === buildingKey) {
        setTotalArea(totalArea - parseFloat(building.area))
      }
      return building.key !== buildingKey
    })
    dispatch(updateSterilizationCompanyCallBuildings(buildingsList))
    if (!isListing || !sterilizationCompanyCallBuildings.length) {
      toggleListing()
    }
  }

  const calculateArea = async () => {
    if (path.length < 3) return

    const area = await window.google.maps.geometry.spherical.computeArea(
      path.map(p => new window.google.maps.LatLng(p.lat, p.lng))
    )
    setActiveBuilding({ ...activeBuilding, area: area.toFixed(2) })
  }

  const resetPath = () => setActiveBuilding({ path: [], area: 0, name: '' })

  const updateBuildingName = e => setActiveBuilding({ ...activeBuilding, name: e.target.value })

  const validateBuildingsList = () => {
    toggleLoading()
    post(`${import.meta.env.VITE_API_URL}/public/sterilization-calls/buildings`,
      renderFormData({ buildings: { ...sterilizationCompanyCallBuildings }, id: sterilizationCallId, freeCode }, ['buildings']),
      response => {
        const { code, sterilizationOrder, paymentModes, paymentPage, validationPage, errors, isToPay } = response
        if (200 === code) {
          dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
          if (isToPay && sterilizationOrder && sterilizationOrder.billedAmount) {
            dispatch(updateSterilizationPayment({ sterilizationOrder, paymentModes, paymentPage }))
            navigate(ROLE_PUBLIC['paymentPage'])
            window.localStorage.setItem('sterilizationOrder', sterilizationOrder.reference)
          } else if (validationPage) {
            dispatch(updateValidationPage(validationPage))
            navigate(ROLE_PUBLIC['publicValidation'])
          }
        } else {
          dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
        }
      })
  }

  const listBuildings = () => {
    const buildings = sterilizationCompanyCallBuildings.map(building => {
      const { name, area, key } = building
      return (
        <Div cssClass='building' key={key}>
          <h5><span>{name}</span>: {area}m² </h5>
          <Div cssClass='options'>
            <Icon
              icon={`icon-eye${activeBuilding.name === building.name ? '-blocked' : ''}`}
              callback={toggleBuilding}
              value={building}
              spanClass='view'
            />
            <Icon
              icon={`icon-bin`}
              callback={removeBuilding}
              value={key}
              spanClass='remove'
            />
          </Div>
        </Div>
      )
    })
    return buildings
  }

  useEffect(() => {
    if (totalArea && callType) {
      setCost(calculateCost(
        totalArea.toFixed(2),
        Object.values(missionBillings).filter(billing => billing.isSurfaceBased),
        callType
      ))
    }
  }, [totalArea, missionBillings])

  useEffect(() => {
    if (!callType) {
      get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-season/${selectedSeason || mission.activeSeason.id}/calls/${sterilizationCallId}`,
        response => {
          const { code, sterilizationCall } = response
          if (200 === code) {
            dispatch(updateSterilizationCompanyCallFormObject(sterilizationCall))
          }
        })
    }
  }, [])

  return (
    <>
      <Div cssClass='buildings-manager'>
        <Div cssClass='manager-header'>
          <p className='description'>
            {t('labels.clic_buildings_angles')}
          </p>
          <Icon callback={toggleModal} icon='icon-lightbulb_outline' spanClass='help-icon' />
        </Div>

        {path.length >= 3 && <p><strong>{t('labels.surface')}:</strong> {area > 0 ? `${area} m²` : t('labels.non_calculates')}</p>}
        <TextType
          value={name || ''}
          onChange={updateBuildingName}
          placeholder={t('form.building_name')}
          cssClass={0 === area ? 'is-hidden' : ''}
        />
        <Div cssClass='buildings-actions'>
          <Buttons buttons={[{
            text: t('actions.calculate_area'),
            disabled: path.length < 3,
            callback: calculateArea,
            cssClass: `is-small is-rounded ${path.length < 3 ? 'is-static' : 'is-info'} ${area > 0 ? 'is-hidden' : ''}`
          }, {
            text: t('actions.add_to_buildings'),
            disabled: 0 === area,
            callback: addToBuildingsList,
            cssClass: `is-small is-rounded ${0 === area ? 'is-static' : 'is-success'} ${0 === area ? 'is-hidden' : ''}`
          }, {
            text: t('actions.reset_building'),
            disabled: 0 === path.length,
            callback: resetPath,
            cssClass: `is-small is-rounded ${path.length < 3 ? 'is-static' : 'is-warning'}`
          }]} />
        </Div>

        <Div cssClass={`buildings-header ${sterilizationCompanyCallBuildings.length ? 'is-filled' : ''}`} otherProps={{ onClick: toggleListing }}>
          <h3>{t('labels.buildings')} <span>({sterilizationCompanyCallBuildings.length})</span></h3>
          <Icon icon={`icon-keyboard_arrow_${isListing ? 'down' : 'up'}`} />
        </Div>
        <Div cssClass={`buildings-list ${isListing ? '' : 'is-hidden'}`} >
          {listBuildings()}
          <Div cssClass='costs'>
            <p><label>{t('labels.total_area')} : </label> <span>{totalArea.toFixed(2)} m²</span></p>
            <p className='total-cost'>
              <label>{t('labels.total_cost')} : </label>
              <span>{cost ? `${cost.toFixed(2)} €` : t('labels.free')}</span>
            </p>
          </Div>
          <TextType
            value={freeCode || ''}
            onChange={e => setFreeCode(e.target.value)}
            placeholder={t('form.free_code')}
            cssClass='is-small'
          />
          <Div cssClass='buildings-list-actions'>
            <Buttons buttons={{
              text: t('actions.validate_buildings_list'),
              disabled: 0 === sterilizationCompanyCallBuildings.length,
              callback: validateBuildingsList,
              cssClass: `is-small is-success validate ${isLoading ? 'is-loading' : ''}`
            }}
            />
          </Div>
        </Div>

      </Div>
      <Modal cssClass='create-element-modal' isActive={isModalActive} closeCallback={toggleModal} closeAction={true}>
        <video src={videoBuildingsMap} controls></video>
      </Modal>
    </>
  )
}

CompanySterilizationCallBuildingsMapManager.propTypes = {
  activeBuilding: object,
  setActiveBuilding: func
}

export default CompanySterilizationCallBuildingsMapManager
